import { PromptCategoryId, PromptDTO } from "./prompt";
import { Nullable } from "./utils";

/**
 * Longest value each field of a tactic proposed from a conversation may carry.
 *
 * One set of numbers for the tool schema, the endpoint that saves the proposal
 * and the review card's inputs — three places that would otherwise drift. The
 * description is the one the catalogue prints (160 characters, beyond which it
 * is cut); the content matches what `activateUserTactic` serves whole.
 */
export const PROPOSED_TACTIC_MAX_CHARS = {
  title: 80,
  description: 160,
  content: 20_000,
} as const;

/**
 * A tactic written by the assistant from a conversation, not yet saved.
 *
 * It is the output of the `proposeTactic` tool and, unchanged, the body of the
 * request that saves it: the author reads it, edits it, and sends back what is
 * on screen. Nothing exists in the library until that request.
 */
export type ProposedTactic = {
  readonly title: string;
  readonly description: string;
  readonly content: string;
  readonly categoryId: PromptCategoryId;
};

/**
 * A proposal as the tool returns it. A revision — the author asked for changes
 * to an earlier proposal — also names the calls it replaces, latest first:
 * saving it updates a tactic saved from any of them instead of adding a copy,
 * and their cards show they were replaced.
 */
export type ProposeTacticToolOutput = ProposedTactic & {
  readonly revisesToolCallIds?: readonly string[];
};

/**
 * The reviewed proposal, plus the one decision the model never makes: whether
 * the assistant may apply the tactic on its own once it is saved.
 */
export type CreatePromptFromConversationDTO = ProposedTactic & {
  readonly isAutoEnabled: boolean;
};

/**
 * Most `proposeTactic` calls one lookup may ask about.
 *
 * The thread asks once for every proposal it holds rather than once per card;
 * this bounds the `IN` list of that one query. A thread holding more asks
 * about its latest ones, and the older cards fall back to the form, whose save
 * returns the tactic already saved rather than a copy.
 */
export const MAX_PROPOSED_TACTIC_LOOKUPS = 100;

/**
 * What a reopened conversation learns about the `proposeTactic` calls it
 * holds: for each call asked about, the tactic saved from it, or null while it
 * has not been saved. A call missing from the map was not asked about.
 */
export type SavedProposedTacticsDTO = {
  readonly prompts: Readonly<Record<string, Nullable<PromptDTO>>>;
};
